import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Zap, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("This reset link is missing its token. Request a new one.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/reset-password", {
        token, new_password: password,
      });
      // Persist the fresh session
      localStorage.setItem("contentos_token", data.access_token);
      localStorage.setItem("contentos_user", JSON.stringify(data.user));
      toast.success("Password updated — you're signed in.");
      // Full reload so the auth context bootstraps from localStorage cleanly
      window.location.href = data.user.onboarded ? "/app" : "/onboarding";
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
              <Zap className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black tracking-tight text-xl">ContentOS</span>
          </Link>
          <div className="rounded-lg border border-border bg-card p-6">
            <AlertTriangle className="h-6 w-6 text-amber-400 mb-3" />
            <h1 className="font-display font-black text-2xl tracking-tight">Missing reset token</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This URL is incomplete. Request a new reset link to continue.
            </p>
            <Link
              to="/auth/forgot"
              className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary hover:underline"
              data-testid="link-request-new"
            >
              Request a new link <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
            <Zap className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black tracking-tight text-xl">ContentOS</span>
        </Link>

        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Choose a new password</div>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">
          Set a new<br />password.
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          After saving, you'll be signed in automatically. All existing reset links for this account will be invalidated.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
          <div>
            <Label className="text-xs uppercase tracking-widest">New password</Label>
            <Input
              data-testid="input-new-password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-11"
            />
            <div className="mt-1 text-xs text-muted-foreground">At least 8 characters.</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Confirm new password</Label>
            <Input
              data-testid="input-confirm-password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 h-11"
            />
          </div>

          {error && (
            <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-400 flex items-start gap-2" data-testid="reset-error">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            data-testid="btn-reset-password"
            type="submit"
            disabled={busy || !password || !confirm}
            className="w-full h-11"
          >
            {busy ? "Saving…" : "Save password & sign in"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

        <Link
          to="/auth"
          className="inline-flex items-center gap-1.5 mt-6 text-sm text-muted-foreground hover:text-foreground"
          data-testid="link-back-to-signin"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
